export interface InstrumentDisplayName {
    acronym: string;
    shortName: string;
}

const ACRONYM_PATTERN = /^[A-Z0-9][A-Z0-9/-]{1,14}$/;

function isAcronymLike(value: string): boolean {
    return ACRONYM_PATTERN.test(value) && /[A-Z]/.test(value);
}

function primaryAcronym(value: string): string {
    return value.split('/')[0];
}

export function parseInstrumentName(name: string, testId: string): InstrumentDisplayName {
    const trimmed = name.trim();

    const trailingParen = trimmed.match(/^(.*\S)\s*\(([^()]+)\)$/);
    if (trailingParen && isAcronymLike(trailingParen[2].trim())) {
        return {
            acronym: primaryAcronym(trailingParen[2].trim()),
            shortName: trailingParen[1]
                .trim()
                .replace(/[—-]\s*$/, '')
                .trim(),
        };
    }

    const leading = trimmed.match(/^([A-Z0-9][A-Z0-9/-]{1,14})\s*(?:\(([^()]+)\)|[—–-]\s+(\S.*))$/);
    if (leading && isAcronymLike(leading[1])) {
        return {
            acronym: primaryAcronym(leading[1]),
            shortName: (leading[2] ?? leading[3]).trim(),
        };
    }

    const trailingDash = trimmed.match(/^(.*\S)\s+[—–-]\s+([A-Z0-9][A-Z0-9/-]{1,14})$/);
    if (trailingDash && isAcronymLike(trailingDash[2])) {
        return {
            acronym: primaryAcronym(trailingDash[2]),
            shortName: trailingDash[1].trim(),
        };
    }

    return {
        acronym: testId.toUpperCase(),
        shortName: trimmed,
    };
}

export interface InstrumentVariant {
    base: string;
    /** Trailing parenthetical, when the name carries one. */
    variant: string | null;
}

/**
 * Splits off the trailing parenthetical `parseInstrumentName` left behind.
 *
 * Whatever survives that call is descriptive rather than an acronym — the
 * acronym forms are already consumed above. Usually it restates something the
 * caller has deliberately left out ("16-30 meses" is the audience), but for a
 * few instruments it is the only thing telling two catalog entries apart:
 * SCARED and SCAS each ship a child version and a parents version that share an
 * acronym *and* a name, differing solely in this suffix. Splitting it off lets
 * the caller show it exactly where it earns its place — see
 * `findAmbiguousAcronyms`.
 */
export function splitVariant(shortName: string): InstrumentVariant {
    const match = shortName.match(/^(.*\S)\s*\(([^()]+)\)$/);
    if (!match) return { base: shortName, variant: null };

    return { base: match[1].trim(), variant: match[2].trim() };
}
